import { useCallback, useEffect, useRef, useState } from "react";
import { GetQuestionnaireResponse } from "@/services/fhir/questionnaireresponse/GetQuestionnaries";
import type { ClinicalInsightDateRange } from "src-next/modules/clinical-insights/types/analysisTypes";
import type { LoadPhase, PatientsProgress } from "src-next/modules/clinical-insights/loadPhase";
import { fetchPatientsParallel } from "src-next/modules/clinical-insights/helpers/fetchPatientsParallel";

export interface ConductData {
  conduct: string;
  status: string;
  practitioner: string;
  date: string;
  patientId: string;
  patientName?: string;
}

interface UseConductAnalysisDataArgs {
  author: string;
  practitionerFilter: string;
  dateRange?: ClinicalInsightDateRange;
  getPractitionerName: (ref: string) => string;
  onLoadPhaseChange?: (phase: LoadPhase) => void;
  fetchEnabled?: boolean;
}

function processEntriesToConducts(
  entries: unknown[],
  getPractitionerName: (ref: string) => string
): ConductData[] {
  const conductMap = new Map<
    string,
    {
      conduct: string;
      status: string;
      practitioner: string;
      date: string;
      patientId: string;
      authored: Date;
    }
  >();

  for (const entry of entries) {
    const resource = (entry as { resource?: fhir5.QuestionnaireResponse })?.resource;
    if (!resource || resource.resourceType !== "QuestionnaireResponse") continue;

    const conductsItem = resource.item?.find(
      (item) => item.linkId === "38adde2c-f083-435f-8a1f-fc9cc6fa740a"
    );
    if (!conductsItem?.item) continue;

    const patientId = resource.subject?.reference || "unknown";
    const practitionerName = getPractitionerName(resource.author?.reference || "");
    const authoredDate = resource.authored ? new Date(resource.authored) : new Date(0);
    const date = authoredDate.toLocaleDateString();
    const seenInThisQuestionnaire = new Set<string>();

    for (const conductGroup of conductsItem.item) {
      if (
        !conductGroup.item ||
        conductGroup.linkId !== "a6f78af6-6fd9-49a7-e606-491065403d2d"
      ) {
        continue;
      }

      const conductItem = conductGroup.item.find(
        (item) => item.linkId === "3cccab72-a2ed-46e9-ebce-a1c6532e4bca"
      );
      const conductText = conductItem?.answer?.[0]?.valueString;
      if (!conductText) continue;

      const statusItem = conductGroup.item.find(
        (item) => item.linkId === "2e202c22-d5bc-4243-870e-d342cb9c1b8d"
      );

      let status = "aberta";
      if (statusItem?.answer?.[0]?.valueCoding) {
        const statusCode = statusItem.answer[0].valueCoding.code;
        const statusDisplay = statusItem.answer[0].valueCoding.display;
        if (statusCode === "concluded" || statusDisplay === "Realizada") {
          status = "realizada";
        } else if (statusCode === "partially" || statusDisplay === "Parcialmente") {
          status = "parcial";
        } else if (statusCode === "failed" || statusDisplay === "Não realizada") {
          status = "não realizada";
        }
      }

      const uniqueKey = status === "aberta" ? conductText : `${conductText}-${status}`;
      if (seenInThisQuestionnaire.has(uniqueKey)) continue;
      seenInThisQuestionnaire.add(uniqueKey);

      const key =
        status === "aberta"
          ? `${patientId}-${conductText}`
          : `${patientId}-${conductText}-${status}`;

      const existing = conductMap.get(key);
      if (!existing || authoredDate > existing.authored) {
        conductMap.set(key, {
          conduct: conductText,
          status,
          practitioner: practitionerName,
          date,
          patientId,
          authored: authoredDate,
        });
      }
    }
  }

  const patientLatestAuthored = new Map<string, number>();
  for (const v of conductMap.values()) {
    const t = v.authored.getTime();
    const prev = patientLatestAuthored.get(v.patientId) || 0;
    if (t > prev) patientLatestAuthored.set(v.patientId, t);
  }

  type ConductEntry = {
    conduct: string;
    status: string;
    practitioner: string;
    date: string;
    patientId: string;
    authored: Date;
  };
  const groups = new Map<string, ConductEntry[]>();
  for (const value of conductMap.values()) {
    const key = `${value.patientId}|${value.conduct}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(value);
  }

  const parityConducts: ConductData[] = [];
  for (const [, items] of groups.entries()) {
    if (items.length === 1) {
      parityConducts.push({
        conduct: items[0].conduct,
        status: items[0].status,
        practitioner: items[0].practitioner,
        date: items[0].date,
        patientId: items[0].patientId,
      });
      continue;
    }

    const sorted = items.sort((a, b) => b.authored.getTime() - a.authored.getTime());
    const isPair = items.length % 2 === 0;
    const withStatus = sorted.filter((it) => it.status && it.status !== "aberta");
    const withoutStatus = sorted.filter((it) => !it.status || it.status === "aberta");
    const latestAuthoredTs = patientLatestAuthored.get(sorted[0].patientId);
    const latestBelongsToCurrentQR =
      latestAuthoredTs !== undefined && sorted[0].authored.getTime() === latestAuthoredTs;

    if (latestBelongsToCurrentQR) {
      for (const item of sorted) {
        parityConducts.push({
          conduct: item.conduct,
          status: item.status,
          practitioner: item.practitioner,
          date: item.date,
          patientId: item.patientId,
        });
      }
    } else if (isPair) {
      if (withStatus.length > 0) {
        parityConducts.push({
          conduct: withStatus[0].conduct,
          status: withStatus[0].status,
          practitioner: withStatus[0].practitioner,
          date: withStatus[0].date,
          patientId: withStatus[0].patientId,
        });
      } else {
        parityConducts.push({
          conduct: sorted[0].conduct,
          status: sorted[0].status,
          practitioner: sorted[0].practitioner,
          date: sorted[0].date,
          patientId: sorted[0].patientId,
        });
      }
    } else if (withStatus.length > 0) {
      parityConducts.push({
        conduct: withStatus[0].conduct,
        status: withStatus[0].status,
        practitioner: withStatus[0].practitioner,
        date: withStatus[0].date,
        patientId: withStatus[0].patientId,
      });
      if (withoutStatus.length > 0) {
        parityConducts.push({
          conduct: withoutStatus[0].conduct,
          status: withoutStatus[0].status,
          practitioner: withoutStatus[0].practitioner,
          date: withoutStatus[0].date,
          patientId: withoutStatus[0].patientId,
        });
      }
    } else {
      parityConducts.push({
        conduct: sorted[0].conduct,
        status: sorted[0].status,
        practitioner: sorted[0].practitioner,
        date: sorted[0].date,
        patientId: sorted[0].patientId,
      });
    }
  }

  return parityConducts;
}

export function useConductAnalysisData({
  author,
  practitionerFilter,
  dateRange,
  getPractitionerName,
  onLoadPhaseChange,
  fetchEnabled = true,
}: UseConductAnalysisDataArgs) {
  const [conducts, setConducts] = useState<ConductData[]>([]);
  const [patients, setPatients] = useState<Map<string, fhir5.Patient>>(new Map());
  const [loadPhase, setLoadPhase] = useState<LoadPhase>("idle");
  const [patientsProgress, setPatientsProgress] = useState<PatientsProgress>({
    loaded: 0,
    total: 0,
  });
  const requestIdRef = useRef(0);
  const fetchEnabledRef = useRef(fetchEnabled);
  fetchEnabledRef.current = fetchEnabled;
  const onLoadPhaseChangeRef = useRef(onLoadPhaseChange);
  onLoadPhaseChangeRef.current = onLoadPhaseChange;
  const getPractitionerNameRef = useRef(getPractitionerName);
  getPractitionerNameRef.current = getPractitionerName;

  const setPhase = useCallback((phase: LoadPhase) => {
    setLoadPhase(phase);
    if (fetchEnabledRef.current) onLoadPhaseChangeRef.current?.(phase);
  }, []);

  const fetchConductsData = useCallback(async () => {
    if (!author) return;

    const requestId = ++requestIdRef.current;
    setPhase("records");

    try {
      const response = await GetQuestionnaireResponse({
        author,
        practitioner: practitionerFilter,
        hasProtocols: false,
        questionnaire: "Questionnaire/clinicalRegister",
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      });

      if (requestId !== requestIdRef.current) return;

      const entries: unknown[] = response?.entry || [];
      const parityConducts = processEntriesToConducts(
        entries,
        getPractitionerNameRef.current
      );
      setConducts(parityConducts);

      const uniquePatientIds = Array.from(new Set(parityConducts.map((c) => c.patientId)));
      if (uniquePatientIds.length === 0) {
        setPatients(new Map());
        setPatientsProgress({ loaded: 0, total: 0 });
        setPhase("done");
        return;
      }

      setPhase("patients");
      const patientMap = await fetchPatientsParallel(uniquePatientIds, {
        requestId,
        isRequestCurrent: (id) => id === requestIdRef.current,
        onProgress: (loaded, total) => {
          if (requestId === requestIdRef.current) {
            setPatientsProgress({ loaded, total });
          }
        },
      });
      if (requestId === requestIdRef.current) {
        setPatients(patientMap);
        setPhase("done");
      }
    } catch (error) {
      console.error("Erro ao buscar condutas:", error);
      if (requestId === requestIdRef.current) {
        setPhase("error");
      }
    }
  }, [author, practitionerFilter, dateRange?.startDate, dateRange?.endDate, setPhase]);

  useEffect(() => {
    if (author && fetchEnabled) {
      fetchConductsData();
    }
  }, [fetchConductsData, author, fetchEnabled]);

  const hasData = conducts.length > 0;

  return {
    conducts,
    patients,
    loadPhase,
    patientsProgress,
    hasData,
    retry: fetchConductsData,
  };
}
